import { Dispatch, RefObject, SetStateAction, useEffect, useRef, useState } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { CREATE_POST, CREATE_POST_TRUSTEE, DELETE_MEDIA, GET_MEDIA, UPDATE_POST } from '@/modules/API/news/news'
import { GET_PARENT_LIST } from '@/modules/API/parent/parentHandler'
import { MediaFile, Poll, PollInput, PollOption, PollOptionInput, Post, PostCategory, PostType } from '@/types/NewsFeed'
import { Parent } from '@/types/Parent'
import { Section } from '@/types/Section'
import { Daycare } from '@/types/Daycare'
import { useNotification } from '@/modules/context/NotificationContext'
import { getMimeTypeFromFileName } from '@/modules/utils/mediaConstants'
import moment from 'moment-timezone'

export interface FilePreview {
    mediaId: number
    mediaType: string
    file: File
    preview: string
}

interface UsePostFormProps {
    daycare: Daycare | undefined
    modifyPost: Post | null
    handleClose: (doRefetch: boolean) => void
    refetch: () => void
    trusteeDaycareIds?: number[]
}

interface UsePostFormReturn {
    modify: boolean
    formReady: boolean
    mediasReady: boolean
    isDirty: boolean
    postId: number
    postMessage: string
    postCategoryIds: number[]
    postChilds: number[]
    postChildsModify: number[]
    sections: Section[]
    parentsList: Parent[]
    filePreview: FilePreview[]
    loadingCreateMedia: boolean
    isLoading: boolean
    disabledButton: boolean
    selectedDaycares: number[]
    selectedSections: number[]
    selectedWithoutSection: number[]
    visibleAll: boolean
    poll: PollInput | undefined
    hasVotes: boolean
    setPostMessage: (message: string) => void
    setPostCategoryIds: Dispatch<SetStateAction<number[]>>
    setPostChilds: Dispatch<SetStateAction<number[]>>
    setFilePreview: Dispatch<SetStateAction<FilePreview[]>>
    setLoadingCreateMedia: (loading: boolean) => void
    setSelectedDaycares: Dispatch<SetStateAction<number[]>>
    setSelectedSections: Dispatch<SetStateAction<number[]>>
    setSelectedWithoutSection: Dispatch<SetStateAction<number[]>>
    setVisibleAll: Dispatch<SetStateAction<boolean>>
    setPoll: Dispatch<SetStateAction<PollInput | undefined>>
    addNewFiles: (files: FilePreview[]) => void
    handleSubmitCreate: (draft?: boolean) => void
    handleUpdate: () => void
    handleCloseAndCleanFile: () => void
    addEmojiToPostMessage: (emoji: string) => void
}

interface RefInitialValue {
    message: string
    categoryIds: number[]
    visibleAll: boolean
    childIds: number[]
    mediaIds: number[]
    poll?: PollInput
}

export function usePostForm({
    daycare,
    modifyPost,
    handleClose,
    refetch,
    trusteeDaycareIds
}: UsePostFormProps): UsePostFormReturn {
    const { t } = useTranslation()
    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [modify, setModify] = useState<boolean>(!!modifyPost)
    const [formReady, setFormReady] = useState<boolean>(!modifyPost)
    const [mediasReady, setMediasReady] = useState<boolean>(!modifyPost)

    const initialValuesRef: RefObject<RefInitialValue | null> = useRef<RefInitialValue | null>(null)
    const [postChildsModify, setPostChildsModify] = useState<number[]>([])
    const [postId, setPostId] = useState<number>(0)
    const [postMessage, setPostMessage] = useState<string>('')
    const [postCategoryIds, setPostCategoryIds] = useState<number[]>([])
    const [postChilds, setPostChilds] = useState<number[]>([])
    const [sections, setSections] = useState<Section[]>([])
    const [parentsList, setParentsList] = useState<Parent[]>([])
    const [filePreview, setFilePreview] = useState<FilePreview[]>([])
    const [loadingCreateMedia, setLoadingCreateMedia] = useState<boolean>(false)

    const [selectedDaycares, setSelectedDaycares] = useState<number[]>([])
    const [selectedSections, setSelectedSections] = useState<number[]>([])
    const [selectedWithoutSection, setSelectedWithoutSection] = useState<number[]>([])
    const [visibleAll, setVisibleAll] = useState<boolean>(false)
    const [poll, setPoll] = useState<PollInput | undefined>(undefined)

    const [getParents] = useLazyQuery(GET_PARENT_LIST)
    const [getMediaBase64] = useLazyQuery(GET_MEDIA)
    const [deleteMedia, { loading: loadingDeleteMedia }] = useMutation(DELETE_MEDIA)

    const [createPost, { loading: loadingCreate }] = useMutation(CREATE_POST, {
        onCompleted: () => {
            resetForm()
            handleClose(true)
            showNotificationSuccess(t('postCreated'))
            refetch()
        },
        onError: () => {
            showNotificationError(t('postCreationError'))
        }
    })

    const [createPostTrustee, { loading: loadingCreateTrustee }] = useMutation(CREATE_POST_TRUSTEE, {
        onCompleted: () => {
            resetForm()
            handleClose(true)
            showNotificationSuccess(t('postCreated'))
            refetch()
        },
        onError: () => {
            showNotificationError(t('postCreationError'))
        }
    })

    const [updatePost, { loading: loadingUpdate }] = useMutation(UPDATE_POST, {
        onCompleted: () => {
            resetForm()
            setModify(false)
            handleClose(true)
            showNotificationSuccess(t('postUpdated'))
            refetch()
        },
        onError: () => {
            showNotificationError(t('postUpdateError'))
        }
    })

    function resetForm(): void {
        setPostChildsModify([])
        setPostId(0)
        setPostMessage('')
        setPostCategoryIds([])
        setPostChilds([])
        setFilePreview([])
        setSelectedDaycares([])
        setSelectedSections([])
        setSelectedWithoutSection([])
        setVisibleAll(false)
        setPoll(undefined)
        setFormReady(true)
        setMediasReady(true)
        initialValuesRef.current = null
    }

    useEffect(() => {
        if (modifyPost && daycare) {
            let cancelled: boolean = false

            setFilePreview([])
            setPostId(modifyPost.id)
            setPostMessage(modifyPost.message)
            setPostCategoryIds(modifyPost.categories?.map((c: PostCategory): number => c.id) ?? [])
            setPostChildsModify(modifyPost.childIds ?? [])
            setVisibleAll(modifyPost.visibleAll ?? false)

            const initialPoll: PollInput | undefined =
                modifyPost.type === PostType.post && modifyPost.poll ? pollToPollInput(modifyPost.poll) : undefined

            setPoll(initialPoll)

            initialValuesRef.current = {
                message: modifyPost.message,
                categoryIds: modifyPost.categories?.map((c: PostCategory): number => c.id) ?? [],
                visibleAll: modifyPost.visibleAll ?? false,
                childIds: [...(modifyPost.childIds ?? [])].sort((a: number, b: number): number => a - b),
                mediaIds: modifyPost.medias
                    .map((m: MediaFile): number => m.media.id)
                    .sort((a: number, b: number): number => a - b),
                poll: initialPoll
            }

            const fetchMediaPreviews: () => void = async (): Promise<void> => {
                const previews: (FilePreview | null)[] = await Promise.all(
                    modifyPost.medias.map(async (mediaFile: MediaFile): Promise<FilePreview | null> => {
                        const result: { data: { getMedia: MediaFile } } = await getMediaBase64({
                            variables: {
                                daycareId: daycare.id,
                                postId: modifyPost.id,
                                mediaId: mediaFile.media.id
                            }
                        })

                        if (result.data?.getMedia) {
                            const media: MediaFile = result.data.getMedia
                            const previewType: string = getMimeTypeFromFileName(media.media.fileName)
                            return {
                                mediaId: media.media.id,
                                mediaType: media.media.type as string,
                                file: new File([], media.media.fileName),
                                preview: `data:${previewType};base64,${media.base64Data}`
                            } as FilePreview
                        }
                        return null
                    })
                )

                const validPreviews: FilePreview[] = previews.filter(
                    (x: FilePreview | null): x is FilePreview => x !== null
                )
                if (!cancelled) {
                    setFilePreview(validPreviews)
                    setMediasReady(true)
                }
            }

            fetchMediaPreviews()
            setModify(true)
            setFormReady(true)

            return () => {
                cancelled = true
            }
        }
    }, [modifyPost])

    useEffect(() => {
        if (daycare?.sections) {
            setSections(daycare.sections)
            getParents({
                variables: { daycare_id: daycare.id }
            }).then((r) => {
                const getParentList: Parent[] = r.data?.getParentList
                if (getParentList) setParentsList(getParentList)
            })
        }
    }, [daycare])

    function addNewFiles(files: FilePreview[]): void {
        setFilePreview([...filePreview, ...files])
    }

    function handleSubmitCreate(draft: boolean = true): void {
        if (visibleAll && trusteeDaycareIds) {
            createPostTrustee({
                variables: {
                    daycareIds: trusteeDaycareIds,
                    sectionIds: [],
                    withoutSectionDaycareIds: [],
                    message: postMessage,
                    categoryIds: postCategoryIds,
                    mediaIds: filePreview.map((file: FilePreview): number => file.mediaId),
                    draft: draft,
                    visibleAll: true,
                    poll
                }
            })
        } else if (visibleAll) {
            createPost({
                variables: {
                    daycareId: daycare?.id,
                    message: postMessage,
                    categoryIds: postCategoryIds,
                    childIds: [],
                    mediaIds: filePreview.map((file: FilePreview): number => file.mediaId),
                    draft: draft,
                    visibleAll: true,
                    poll
                }
            })
        } else if (postChilds.length > 0) {
            createPost({
                variables: {
                    daycareId: daycare?.id,
                    message: postMessage,
                    categoryIds: postCategoryIds,
                    childIds: postChilds,
                    mediaIds: filePreview.map((file: FilePreview): number => file.mediaId),
                    draft: draft,
                    visibleAll: false,
                    poll
                }
            })
        } else if (selectedSections.length > 0 || selectedWithoutSection.length > 0) {
            createPostTrustee({
                variables: {
                    daycareIds: selectedDaycares,
                    sectionIds: selectedSections,
                    withoutSectionDaycareIds: selectedWithoutSection,
                    message: postMessage,
                    categoryIds: postCategoryIds,
                    mediaIds: filePreview.map((file: FilePreview): number => file.mediaId),
                    draft: draft,
                    visibleAll: false,
                    poll
                }
            })
        } else {
            showNotificationError(t('noChildSelected'))
        }
    }

    function handleUpdate(): void {
        if (!visibleAll && postChilds.length === 0) {
            showNotificationError(t('noChildSelected'))
            return
        }
        updatePost({
            variables: {
                daycareId: daycare?.id,
                postId: postId,
                message: postMessage,
                categoryIds: postCategoryIds,
                childIds: postChilds,
                mediaIds: filePreview.map((file: FilePreview): number => file.mediaId),
                visibleAll: visibleAll,
                poll
            }
        })
    }

    function pollToPollInput(dbPoll: Poll): PollInput {
        return {
            type: dbPoll.type,
            options: dbPoll.options?.map((opt: PollOption): PollOptionInput => ({ text: opt.text, order: opt.order })),
            deadline: dbPoll.deadline ? moment(dbPoll.deadline).format('DD-MM-YYYY HH:mm') : undefined,
            ratingDisplay: dbPoll.ratingDisplay
        }
    }

    function handleCloseAndCleanFile(): void {
        if (filePreview.length > 0) {
            if (!modify) {
                void Promise.all(
                    filePreview.map((file: FilePreview) =>
                        deleteMedia({ variables: { mediaId: file.mediaId, daycareId: daycare?.id } })
                    )
                ).catch(() => showNotificationError(t('deleteMediaError')))
            } else if (initialValuesRef.current) {
                const originalMediaIds: Set<number> = new Set(initialValuesRef.current.mediaIds)
                const addedMedias: FilePreview[] = filePreview.filter(
                    (f: FilePreview): boolean => !originalMediaIds.has(f.mediaId)
                )
                if (addedMedias.length > 0) {
                    void Promise.all(
                        addedMedias.map((file: FilePreview) =>
                            deleteMedia({ variables: { mediaId: file.mediaId, daycareId: daycare?.id } })
                        )
                    ).catch(() => showNotificationError(t('deleteMediaError')))
                }
            }
        }

        filePreview.forEach((file: FilePreview) => {
            URL.revokeObjectURL(file.preview)
        })
        setFilePreview([])

        handleClose(false)
    }

    function addEmojiToPostMessage(emoji: string): void {
        setPostMessage((prev: string): string => prev + emoji)
    }

    const isLoading: boolean =
        loadingCreate || loadingUpdate || loadingDeleteMedia || loadingCreateMedia || loadingCreateTrustee

    const isDirty: boolean = ((): boolean => {
        if (!modify || !initialValuesRef.current) return false

        const init: RefInitialValue = initialValuesRef.current
        if (postMessage !== init.message) return true
        if (visibleAll !== init.visibleAll) return true

        const currentChildIds: number[] = [...postChilds].sort((a: number, b: number): number => a - b)
        if (JSON.stringify(currentChildIds) !== JSON.stringify(init.childIds)) return true

        const currentCategoryIds: number[] = [...postCategoryIds].sort((a: number, b: number): number => a - b)
        const initCategoryIds: number[] = [...init.categoryIds].sort((a: number, b: number): number => a - b)
        if (JSON.stringify(currentCategoryIds) !== JSON.stringify(initCategoryIds)) return true

        const currentMediaIds: number[] = filePreview
            .map((f: FilePreview): number => f.mediaId)
            .sort((a: number, b: number): number => a - b)
        if (JSON.stringify(currentMediaIds) !== JSON.stringify(init.mediaIds)) return true

        return JSON.stringify(poll) !== JSON.stringify(init.poll)
    })()

    const hasVotes: boolean = !!(modifyPost && 'poll' in modifyPost && modifyPost.poll?.hasVotes)

    const hasValidSelection: boolean = visibleAll || postChilds.length > 0

    const disabledButton: boolean =
        isLoading ||
        (modify
            ? !isDirty || !hasValidSelection
            : !visibleAll &&
              postChilds.length === 0 &&
              selectedSections.length === 0 &&
              selectedWithoutSection.length === 0)

    return {
        modify,
        formReady,
        mediasReady,
        isDirty,
        postId,
        postMessage,
        postCategoryIds,
        postChilds,
        postChildsModify,
        sections,
        parentsList,
        filePreview,
        loadingCreateMedia,
        isLoading,
        disabledButton,
        selectedDaycares,
        selectedSections,
        selectedWithoutSection,
        visibleAll,
        poll,
        hasVotes,
        setPostMessage,
        setPostCategoryIds,
        setPostChilds,
        setFilePreview,
        setLoadingCreateMedia,
        setSelectedDaycares,
        setSelectedSections,
        setSelectedWithoutSection,
        setVisibleAll,
        setPoll,
        addNewFiles,
        handleSubmitCreate,
        handleUpdate,
        handleCloseAndCleanFile,
        addEmojiToPostMessage
    }
}
